/**
 * Knowledge mutations: documents, faqs, gaps.
 *
 * NON-OPTIMISTIC throughout. Knowledge content governs what the AI says to
 * guests, and approve/answer endpoints record the session approver — these are
 * approval actions, not low-stakes toggles, so the UI waits for the backend and
 * then writes the returned canonical entity into the cache.
 */

import { type UseMutationResult, useMutation, useQueryClient } from '@tanstack/react-query';
import { unwrap } from '../queries/_helpers';
import { queryKeys } from '../query-keys';
import { useApiClient } from '../react/ApiClientProvider';
import {
  type Faq,
  faqSchema,
  type KnowledgeDocument,
  type KnowledgeGap,
  knowledgeDocumentSchema,
  knowledgeGapSchema,
} from '../responses';
import type {
  AnswerGapBody,
  CreateDocumentBody,
  CreateFaqBody,
  EditDocumentBody,
  ResolveGapBody,
} from './_types';

// --- Documents ---

function useDocumentSync(tenantId: string, hotelId: string) {
  const queryClient = useQueryClient();
  return (doc: KnowledgeDocument) => {
    queryClient.setQueryData(queryKeys.knowledgeDocument(tenantId, hotelId, doc.id), doc);
    void queryClient.invalidateQueries({ queryKey: queryKeys.knowledge(tenantId, hotelId) });
  };
}

/** `POST /knowledge/documents` — create a knowledge document. */
export function useCreateKnowledgeDocument(
  tenantId: string,
  hotelId: string,
): UseMutationResult<KnowledgeDocument, unknown, CreateDocumentBody> {
  const client = useApiClient();
  const sync = useDocumentSync(tenantId, hotelId);
  return useMutation({
    mutationFn: async (body: CreateDocumentBody) => {
      const result = await client.POST(
        '/api/v1/tenants/{tenantId}/hotels/{hotelId}/knowledge/documents',
        { params: { path: { tenantId, hotelId } }, body, parseAs: 'json' },
      );
      return unwrap(result, knowledgeDocumentSchema);
    },
    onSuccess: sync,
  });
}

export interface EditDocumentVars {
  documentId: string;
  body: EditDocumentBody;
}

/** `PATCH /knowledge/documents/:documentId`. */
export function useEditKnowledgeDocument(
  tenantId: string,
  hotelId: string,
): UseMutationResult<KnowledgeDocument, unknown, EditDocumentVars> {
  const client = useApiClient();
  const sync = useDocumentSync(tenantId, hotelId);
  return useMutation({
    mutationFn: async ({ documentId, body }: EditDocumentVars) => {
      const result = await client.PATCH(
        '/api/v1/tenants/{tenantId}/hotels/{hotelId}/knowledge/documents/{documentId}',
        { params: { path: { tenantId, hotelId, documentId } }, body, parseAs: 'json' },
      );
      return unwrap(result, knowledgeDocumentSchema);
    },
    onSuccess: sync,
  });
}

/** Internal: document lifecycle POST (approve/deactivate/reactivate/archive). */
function useDocumentLifecycle(
  tenantId: string,
  hotelId: string,
  segment: 'approve' | 'deactivate' | 'reactivate' | 'archive',
): UseMutationResult<KnowledgeDocument, unknown, { documentId: string }> {
  const client = useApiClient();
  const sync = useDocumentSync(tenantId, hotelId);
  return useMutation({
    mutationFn: async ({ documentId }: { documentId: string }) => {
      const path =
        `/api/v1/tenants/{tenantId}/hotels/{hotelId}/knowledge/documents/{documentId}/${segment}` as
          | '/api/v1/tenants/{tenantId}/hotels/{hotelId}/knowledge/documents/{documentId}/approve'
          | '/api/v1/tenants/{tenantId}/hotels/{hotelId}/knowledge/documents/{documentId}/deactivate'
          | '/api/v1/tenants/{tenantId}/hotels/{hotelId}/knowledge/documents/{documentId}/reactivate'
          | '/api/v1/tenants/{tenantId}/hotels/{hotelId}/knowledge/documents/{documentId}/archive';
      const result = await client.POST(path, {
        params: { path: { tenantId, hotelId, documentId } },
        parseAs: 'json',
      });
      return unwrap(result, knowledgeDocumentSchema);
    },
    onSuccess: sync,
  });
}

/** `POST /knowledge/documents/:documentId/approve` — records the session approver. */
export function useApproveKnowledgeDocument(tenantId: string, hotelId: string) {
  return useDocumentLifecycle(tenantId, hotelId, 'approve');
}
/** `POST /knowledge/documents/:documentId/deactivate`. */
export function useDeactivateKnowledgeDocument(tenantId: string, hotelId: string) {
  return useDocumentLifecycle(tenantId, hotelId, 'deactivate');
}
/** `POST /knowledge/documents/:documentId/reactivate`. */
export function useReactivateKnowledgeDocument(tenantId: string, hotelId: string) {
  return useDocumentLifecycle(tenantId, hotelId, 'reactivate');
}
/** `POST /knowledge/documents/:documentId/archive`. */
export function useArchiveKnowledgeDocument(tenantId: string, hotelId: string) {
  return useDocumentLifecycle(tenantId, hotelId, 'archive');
}

// --- FAQs ---

/** `POST /knowledge/faqs` — create an approved FAQ entry. */
export function useCreateFaq(
  tenantId: string,
  hotelId: string,
): UseMutationResult<Faq, unknown, CreateFaqBody> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateFaqBody) => {
      const result = await client.POST(
        '/api/v1/tenants/{tenantId}/hotels/{hotelId}/knowledge/faqs',
        { params: { path: { tenantId, hotelId } }, body, parseAs: 'json' },
      );
      return unwrap(result, faqSchema);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.knowledge(tenantId, hotelId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.knowledgeGaps(tenantId, hotelId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.faqsPrefix(tenantId, hotelId) });
    },
  });
}

// --- Gaps ---

function useGapSync(tenantId: string, hotelId: string) {
  const queryClient = useQueryClient();
  return (gap: KnowledgeGap) => {
    queryClient.setQueryData(queryKeys.knowledgeGap(tenantId, hotelId, gap.id), gap);
    void queryClient.invalidateQueries({ queryKey: queryKeys.knowledgeGaps(tenantId, hotelId) });
  };
}

export interface ResolveGapVars {
  gapId: string;
  body: ResolveGapBody;
}

/** `POST /knowledge/gaps/:gapId/resolve` — link a gap to an existing FAQ (approval). */
export function useResolveGap(
  tenantId: string,
  hotelId: string,
): UseMutationResult<KnowledgeGap, unknown, ResolveGapVars> {
  const client = useApiClient();
  const sync = useGapSync(tenantId, hotelId);
  return useMutation({
    mutationFn: async ({ gapId, body }: ResolveGapVars) => {
      const result = await client.POST(
        '/api/v1/tenants/{tenantId}/hotels/{hotelId}/knowledge/gaps/{gapId}/resolve',
        { params: { path: { tenantId, hotelId, gapId } }, body, parseAs: 'json' },
      );
      return unwrap(result, knowledgeGapSchema);
    },
    onSuccess: sync,
  });
}

/** Internal: a no-body gap state change (dismiss/duplicate). */
function useGapLifecycle(
  tenantId: string,
  hotelId: string,
  segment: 'dismiss' | 'duplicate',
): UseMutationResult<KnowledgeGap, unknown, { gapId: string }> {
  const client = useApiClient();
  const sync = useGapSync(tenantId, hotelId);
  return useMutation({
    mutationFn: async ({ gapId }: { gapId: string }) => {
      const path =
        `/api/v1/tenants/{tenantId}/hotels/{hotelId}/knowledge/gaps/{gapId}/${segment}` as
          | '/api/v1/tenants/{tenantId}/hotels/{hotelId}/knowledge/gaps/{gapId}/dismiss'
          | '/api/v1/tenants/{tenantId}/hotels/{hotelId}/knowledge/gaps/{gapId}/duplicate';
      const result = await client.POST(path, {
        params: { path: { tenantId, hotelId, gapId } },
        parseAs: 'json',
      });
      return unwrap(result, knowledgeGapSchema);
    },
    onSuccess: sync,
  });
}

/** `POST /knowledge/gaps/:gapId/dismiss`. */
export function useDismissGap(tenantId: string, hotelId: string) {
  return useGapLifecycle(tenantId, hotelId, 'dismiss');
}
/** `POST /knowledge/gaps/:gapId/duplicate` — mark a gap as a duplicate. */
export function useMarkGapDuplicate(tenantId: string, hotelId: string) {
  return useGapLifecycle(tenantId, hotelId, 'duplicate');
}

export interface AnswerGapVars {
  gapId: string;
  body: AnswerGapBody;
}

/**
 * `POST /knowledge/gaps/:gapId/answer` — answer a gap, creating an FAQ (approval).
 * Returns the new FAQ; invalidates both gaps and knowledge lists.
 */
export function useAnswerGap(
  tenantId: string,
  hotelId: string,
): UseMutationResult<Faq, unknown, AnswerGapVars> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ gapId, body }: AnswerGapVars) => {
      const result = await client.POST(
        '/api/v1/tenants/{tenantId}/hotels/{hotelId}/knowledge/gaps/{gapId}/answer',
        { params: { path: { tenantId, hotelId, gapId } }, body, parseAs: 'json' },
      );
      return unwrap(result, faqSchema);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.knowledgeGaps(tenantId, hotelId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.knowledge(tenantId, hotelId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.faqsPrefix(tenantId, hotelId) });
    },
  });
}
